import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const familyNav = [
  { to: "/portal", label: "Overview", icon: "LayoutDashboard", end: true },
  { to: "/portal/meetings", label: "Meetings", icon: "Video" },
  { to: "/portal/documents", label: "Documents", icon: "FileText" },
  { to: "/portal/messages", label: "Messages", icon: "MessagesSquare" },
  { to: "/portal/notifications", label: "Notifications", icon: "Bell" },
];
const clientNav = [
  { to: "/portal", label: "Overview", icon: "LayoutDashboard", end: true },
  { to: "/portal/roadmap", label: "Roadmap", icon: "Map" },
  { to: "/portal/meetings", label: "Meetings", icon: "Video" },
  { to: "/portal/checklist", label: "Checklist", icon: "ListChecks" },
  { to: "/portal/documents", label: "Documents", icon: "FileText" },
  { to: "/portal/messages", label: "Messages", icon: "MessagesSquare" },
  { to: "/portal/referrals", label: "Referrals", icon: "Handshake" },
  { to: "/portal/notifications", label: "Notifications", icon: "Bell" },
  { to: "/portal/profile", label: "Profile", icon: "UserRound" },
];
const partnerNav = [
  { to: "/portal", label: "Overview", icon: "LayoutDashboard", end: true },
  { to: "/portal/referrals", label: "Referrals", icon: "ListChecks" },
  { to: "/portal/tasks", label: "Tasks", icon: "CheckSquare" },
  { to: "/portal/documents", label: "Documents", icon: "FileText" },
  { to: "/portal/notes", label: "Notes", icon: "StickyNote" },
  { to: "/portal/payouts", label: "Payouts", icon: "DollarSign" },
  { to: "/portal/notifications", label: "Notifications", icon: "Bell" },
  { to: "/portal/profile", label: "Profile", icon: "Building2" },
];
const purchaserNav = [
  { to: "/portal", label: "Overview", icon: "LayoutDashboard", end: true },
  { to: "/portal/opportunities", label: "Opportunities", icon: "Building2" },
  { to: "/portal/purchaser-documents", label: "Documents", icon: "FileText" },
  { to: "/portal/deal-room", label: "Deal Room", icon: "MessagesSquare" },
  { to: "/portal/notifications", label: "Notifications", icon: "Bell" },
];

const portalTypeOf = (role) =>
  role === "partner_vendor" ? "partner" : role === "purchaser_portal_user" ? "purchaser" : role === "client" ? "client" : "family";

const NAV_BY_TYPE = { family: familyNav, client: clientNav, partner: partnerNav, purchaser: purchaserNav };
const LABEL_BY_TYPE = { family: "Family Portal", client: "Family Portal", partner: "Partner Portal", purchaser: "Investor Portal" };

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...p} />;
};

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const type = portalTypeOf(user?.role);
  const nav = NAV_BY_TYPE[type];
  const portalLabel = LABEL_BY_TYPE[type];
  const initials = (user?.full_name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  const isFamily = type === "family" || type === "client";

  return (
    <div className="min-h-screen" style={{ backgroundColor: isFamily ? "#F4F8FB" : "#F7FAFC" }}>
      <header className="sticky top-0 z-30 border-b backdrop-blur" style={{ backgroundColor: "rgba(253,253,254,0.95)", borderColor: "var(--border)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Logo size={36} withText />
          <span className="hidden sm:inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ml-1"
            style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }} data-testid="portal-label">{portalLabel}</span>
          <div className="ml-auto flex items-center gap-1">
            <AccessibilityToolbar />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[rgba(28,67,88,0.06)] transition-colors" data-testid="portal-user-menu">
                  <Avatar className="h-8 w-8"><AvatarFallback style={{ backgroundColor: "var(--mmm-navy)", color: "white", fontSize: 12 }}>{initials}</AvatarFallback></Avatar>
                  <span className="hidden sm:block text-sm font-medium text-[#252221] max-w-[140px] truncate">{user?.full_name}</span>
                  <Icons.ChevronDown size={15} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-[#9C1E22]" data-testid="portal-logout-button">
                  <Icons.LogOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 overflow-x-auto mmm-scroll">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive ? "border-[#9C1E22] text-[#9C1E22]" : "border-transparent text-muted-foreground hover:text-[#1C4358]"
                  }`
                }>
                <Icon name={item.icon} size={16} /> {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 mmm-fade-up">
        <Outlet />
      </main>
    </div>
  );
}
